import React, { useState } from "react";
import {
  Card, CardContent, Box, Typography, Chip, Fade,
  alpha, useTheme, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditIcon from "@mui/icons-material/Edit";
import { KnowledgehubFolderResponse } from "../../../../app/models/responseModels/knowledgehubResponse";
import { useSelector } from "react-redux";
import { RootState } from "../../../../app/features/store/store";
import { PAGES } from "../../../../enums/PagesEnum";

type Props = {
  folder: KnowledgehubFolderResponse;
  index: number;
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  onDelete: (folder: KnowledgehubFolderResponse) => void;
  onUpload: (folder: KnowledgehubFolderResponse) => void;
  onEdit: (folder: KnowledgehubFolderResponse) => void;
};

export default function KnowledgehubFolderCard({
  folder, index, isHovered, onMouseEnter, onMouseLeave, onClick, onDelete, onUpload, onEdit,
}: Props) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
    onDelete(folder);
  };

  const handleUpload = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
    onUpload(folder);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
    onEdit(folder);
  };
  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.KNOWLEDGE_HUB
    )
  );

  const IsCreatePermission = pagePermission
    ? pagePermission.isCreatePermission
    : false;

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
        "Administrators"
      )
      : false
  );

  return (
    <Fade in timeout={300 + index * 50}>
      <Card
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
        sx={{
          cursor: "pointer",
          borderRadius: 3,
          transition: "all 0.2s ease",
          transform: isHovered ? "translateY(-4px)" : "none",
          boxShadow: isHovered
            ? `0px 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            : "0px 2px 8px rgba(0, 0, 0, 0.05)",
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          "&:hover": { borderColor: alpha(theme.palette.primary.main, 0.3) },
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <FolderIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 1.5 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {folder.name}
              </Typography>
              {folder.description && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {folder.description}
                </Typography>
              )}
            </Box>

            {/* Three-dot menu */}
            {IsCreatePermission && IsAdmin && (<IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{ ml: 0.5, color: "text.secondary" }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>)}
          </Box>

          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Chip
              label={`${folder.children?.length ?? 0} folder${folder.children?.length !== 1 ? "s" : ""}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${folder.files?.length ?? 0} file${folder.files?.length !== 1 ? "s" : ""}`}
              size="small"
              variant="outlined"
            />
          </Box>
        </CardContent>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          PaperProps={{ sx: { borderRadius: 2, minWidth: 150, boxShadow: "0px 4px 16px rgba(0,0,0,0.12)" } }}
        >
          <MenuItem onClick={handleEdit}>
            <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText>Access Management</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleUpload}>
            <ListItemIcon><UploadFileIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText>Upload File</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
            <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: "error.main" }} /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      </Card>
    </Fade>
  );
}
